/**
 * TappableSpineWidget — A compact spine visual for the Journey So Far
 * dashboard where tapping any vertebra plays a short Michael V3
 * affirmation about that exact spot (generated via ElevenLabs, cached
 * server-side after first play).
 */

import { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const VERTEBRAE = [
  { id: 'L1', y: 0.10 },
  { id: 'L2', y: 0.24 },
  { id: 'L3', y: 0.38 },
  { id: 'L4', y: 0.52 },
  { id: 'L5', y: 0.66 },
  { id: 'S1', y: 0.84 },
];

const TappableSpineWidget = () => {
  const [playing, setPlaying] = useState(null); // vertebra id
  const [loading, setLoading] = useState(null);
  const [texts, setTexts] = useState({});
  const audioRef = useRef(null);

  const playVertebra = async (id) => {
    // Stop any existing playback first
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* audio already stopped */ }
    }
    setLoading(id);
    // Fetch the affirmation text once so we can show the caption
    if (!texts[id]) {
      try {
        const r = await fetch(`${API}/api/healing/spinal-ease/vertebra-affirmations`);
        if (r.ok) {
          const d = await r.json();
          const map = {};
          (d.affirmations || []).forEach(a => { map[a.vertebra] = a.text; });
          setTexts(map);
        }
      } catch { /* graceful — audio still plays */ }
    }
    const audio = new Audio(`${API}/api/healing/spinal-ease/vertebra-affirmations/${id}/audio`);
    audioRef.current = audio;
    audio.onended = () => { setPlaying(null); setLoading(null); };
    audio.onerror = () => { setPlaying(null); setLoading(null); };
    audio.onplaying = () => { setPlaying(id); setLoading(null); };
    try {
      await audio.play();
    } catch {
      setLoading(null);
    }
  };

  const stop = () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch { /* audio already stopped */ } }
    setPlaying(null); setLoading(null);
  };

  const activeCaption = useMemo(() => texts[playing] || null, [playing, texts]);

  return (
    <div
      className="rounded-2xl p-5 mb-6 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, rgba(30,22,14,0.5) 0%, rgba(10,8,6,0.5) 100%)',
        border: '1px solid rgba(220,180,110,0.16)',
      }}
      data-testid="tappable-spine-widget"
    >
      <p
        className="text-white/45 text-[10px] tracking-[0.3em] uppercase mb-3"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        Tap a vertebra to hear Michael speak to it
      </p>

      <div className="flex items-center gap-5">
        <svg viewBox="0 0 100 300" className="h-56 shrink-0" fill="none">
          {/* Soft central beam */}
          <line x1="50" y1="20" x2="50" y2="280" stroke="rgba(255,220,140,0.35)" strokeWidth="1.2" strokeLinecap="round" />
          <line x1="50" y1="20" x2="50" y2="280" stroke="rgba(255,240,200,0.15)" strokeWidth="4" strokeLinecap="round" />
          {VERTEBRAE.map((v) => {
            const cy = 30 + v.y * 240;
            const isPlaying = playing === v.id;
            const isLoading = loading === v.id;
            return (
              <g key={v.id} style={{ cursor: 'pointer' }} onClick={() => playVertebra(v.id)}>
                <circle
                  cx={50} cy={cy}
                  r={16}
                  fill="transparent"
                  data-testid={`vertebra-tap-${v.id}`}
                />
                <motion.circle
                  cx={50} cy={cy}
                  r={isPlaying ? 11 : 8}
                  fill={isPlaying ? 'rgba(255,240,200,1)' : 'rgba(220,180,110,0.8)'}
                  animate={isPlaying ? {
                    r: [11, 13, 11],
                    opacity: [0.9, 1, 0.9],
                  } : (isLoading ? { opacity: [0.4, 0.9, 0.4] } : { opacity: 0.85 })}
                  transition={isPlaying
                    ? { duration: 1.4, repeat: Infinity, ease: 'easeInOut' }
                    : (isLoading ? { duration: 1.1, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.4 })
                  }
                  style={{ filter: `drop-shadow(0 0 ${isPlaying ? 12 : 5}px rgba(255,220,140,${isPlaying ? 1 : 0.5}))` }}
                />
                <text
                  x={22}
                  y={cy + 4}
                  fill={isPlaying ? 'rgba(255,240,200,0.95)' : 'rgba(230,215,180,0.5)'}
                  fontSize="10"
                  fontFamily="Manrope, sans-serif"
                  textAnchor="end"
                  letterSpacing="0.25em"
                >
                  {v.id}
                </text>
              </g>
            );
          })}
        </svg>

        <div className="flex-1 min-w-0">
          {playing && activeCaption ? (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              key={playing}
              data-testid="vertebra-caption"
            >
              <p
                className="text-white/45 text-[10px] tracking-[0.3em] uppercase mb-2"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Now playing · {playing}
              </p>
              <p
                className="text-white/85 text-[13px] leading-relaxed"
                style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
              >
                {activeCaption}
              </p>
              <button
                onClick={stop}
                className="mt-3 text-white/40 text-[11px] tracking-[0.2em] uppercase hover:text-white/70 transition-colors"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="vertebra-stop"
              >
                Stop
              </button>
            </motion.div>
          ) : loading ? (
            <p
              className="text-white/45 text-[12px]"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
            >
              Michael is preparing your affirmation…
            </p>
          ) : (
            <p
              className="text-white/45 text-[12px] leading-relaxed"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
            >
              Each vertebra carries its own softness.
              Tap one and Michael will speak to it directly.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TappableSpineWidget;
